import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, Button, FormControl, FormLabel,
  Input, Select, Switch, VStack, FormErrorMessage, useToast,
  InputGroup, InputRightElement, IconButton
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const UserModal = ({ isOpen, onClose, user, onSave, roleLabels }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    phone: '',
    isActive: true,
    isForwardingRecipient: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const toast = useToast();

  useEffect(() => {
    if (user) {
      // Edit mode - populate form with user data
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't populate password on edit
        role: user.role || '',
        phone: user.phone || '',
        isActive: user.isActive !== undefined ? user.isActive : true,
        isForwardingRecipient: user.isForwardingRecipient || false
      });
    } else {
      // Create mode - reset form
      setFormData({
        name: '',
        email: '',
        password: '',
        role: '',
        phone: '',
        isActive: true,
        isForwardingRecipient: false
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Password validation (only required for new users)
    if (!user && !formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!user && formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Função é obrigatória';
    }

    // Phone validation (optional but format check if provided)
    if (formData.phone && !/^[\d\s()+-]+$/.test(formData.phone)) {
      newErrors.phone = 'Telefone inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    // Prepare data for submission
    const submitData = { ...formData };
    
    // Remove password if empty (for edit mode)
    if (user && !submitData.password) {
      delete submitData.password;
    }

    try {
      await onSave(submitData);
      handleClose();
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: '',
      phone: '',
      isActive: true,
      isForwardingRecipient: false
    });
    setErrors({});
    setShowPassword(false);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSwitchChange = (e) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: 'full', md: 'md' }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {user ? 'Editar Usuário' : 'Novo Usuário'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.name}>
              <FormLabel>Nome</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite o nome completo"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite o email"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FormLabel>
                Senha {user && '(deixe em branco para não alterar)'}
              </FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={user ? 'Nova senha (opcional)' : 'Digite a senha'}
                />
                <InputRightElement>
                  <IconButton
                    icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.role}>
              <FormLabel>Função</FormLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                placeholder="Selecione uma função"
              >
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors.role}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.phone}>
              <FormLabel>Telefone</FormLabel>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
              />
              <FormErrorMessage>{errors.phone}</FormErrorMessage>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="isActive" mb="0">
                Usuário ativo
              </FormLabel>
              <Switch
                id="isActive"
                isChecked={formData.isActive}
                onChange={handleSwitchChange}
                colorScheme="purple"
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="isForwardingRecipient" mb="0">
                Receber emails encaminhados
              </FormLabel>
              <Switch
                id="isForwardingRecipient"
                isChecked={formData.isForwardingRecipient}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  isForwardingRecipient: e.target.checked
                }))}
                colorScheme="green"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Salvando..."
          >
            {user ? 'Atualizar' : 'Criar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserModal;
